import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { coursesApi } from '../services/coursesApi';
import { CONFIG } from '../../../config';
import { previousSectionOf } from '../utils/sectionNavigation';

function looksLikeNotEnrolled(err) {
  const msg = String(err?.message || '').toLowerCase();
  return msg.includes('not enrolled') || msg.includes('not found') || msg.includes('not authorized');
}

function outlineShape(o) {
  if (!o?.lessons) return [];
  return o.lessons
    .slice()
    .sort((a, b) => a.order - b.order)
    .map(l => ({
      lessonId: l.lessonId,
      order: l.order,
      sections: (l.sections || [])
        .slice()
        .sort((a, b) => a.order - b.order)
        .map(s => ({ sectionId: s.sectionId, order: s.order }))
    }));
}

function outlinesDifferent(a, b) {
  const A = outlineShape(a);
  const B = outlineShape(b);
  if (A.length !== B.length) return true;
  for (let i = 0; i < A.length; i++) {
    if (A[i].lessonId !== B[i].lessonId || A[i].order !== B[i].order) return true;
    const As = A[i].sections, Bs = B[i].sections;
    if (As.length !== Bs.length) return true;
    for (let j = 0; j < As.length; j++) {
      if (As[j].sectionId !== Bs[j].sectionId || As[j].order !== Bs[j].order) return true;
    }
  }
  return false;
}

export function useCourseLearn({
  courseId,
  initialSectionId,
  authEnabled,
  user,
  ready,
  pollOutlineMs = 30000, // 30s polling; pass 0/false to disable
  onNewContent,
}) {
  const [outline, setOutline] = useState(null);
  const [loadingOutline, setLoadingOutline] = useState(false);
  const [lessonView, setLessonView] = useState(null);
  const [loadingLesson, setLoadingLesson] = useState(false);

  // "soft" loaders: used when we already have content and are updating in the background
  const [softLoadingOutline, setSoftLoadingOutline] = useState(false);
  const [softLoadingLesson, setSoftLoadingLesson] = useState(false);

  const [selectedSectionId, setSelectedSectionId] = useState(initialSectionId || null);
  const [status, setStatus] = useState('');
  const [ownershipWarning, setOwnershipWarning] = useState('');

  // Stabilize userId so we don't churn unnecessarily
  const userId = useMemo(() => {
    return authEnabled ? (user?.uid || '') : CONFIG.USER_ID;
  }, [authEnabled, user?.uid]);

  const prevUserIdRef = useRef(userId);
  const userIdChanged = prevUserIdRef.current !== userId;
  useEffect(() => { prevUserIdRef.current = userId; }, [userId]);

  const safeFetchOutline = useCallback(async (uId) => {
    try {
      return await coursesApi.fetchOutline({ userId: uId, courseId, auth: authEnabled });
    } catch (err) {
      if (authEnabled && looksLikeNotEnrolled(err)) {
        setOwnershipWarning('This course may belong to a different account. Showing a read-only view.');
        return await coursesApi.fetchOutline({ userId: CONFIG.USER_ID, courseId, auth: false });
      }
      throw err;
    }
  }, [authEnabled, courseId]);

  const safeFetchLesson = useCallback(async (uId, lessonId) => {
    try {
      return await coursesApi.fetchLesson({ userId: uId, courseId, lessonId, auth: authEnabled });
    } catch (err) {
      if (authEnabled && looksLikeNotEnrolled(err)) {
        setOwnershipWarning('This lesson may belong to a different account. Showing a read-only view.');
        return await coursesApi.fetchLesson({ userId: CONFIG.USER_ID, courseId, lessonId, auth: false });
      }
      throw err;
    }
  }, [authEnabled, courseId]);

  // Load outline + first lesson (no flicker)
  useEffect(() => {
    let alive = true;

    // If auth is required, bail out quietly until it's ready (no loader flips).
    if (authEnabled && (!ready || !user)) return;

    (async () => {
      try {
        // If we already have an outline, do a soft refresh; otherwise, a hard (initial) load.
        const hardOutlineLoad = !outline || userIdChanged;
        if (hardOutlineLoad) {
          setLoadingOutline(true);
        } else {
          setSoftLoadingOutline(true);
        }

        const outlineRes = await safeFetchOutline(userId);
        if (!alive) return;
        setOutline(outlineRes);

        // Determine which lesson to load
        const sortedLessons = outlineRes?.lessons?.slice().sort((a, b) => a.order - b.order) || [];
        const firstLesson = sortedLessons[0];
        const firstSec = firstLesson?.sections?.slice().sort((a, b) => a.order - b.order)[0];

        // If initialSectionId is provided, find which lesson contains it
        let targetLesson = firstLesson;
        let targetSectionId = initialSectionId || firstSec?.sectionId;

        if (initialSectionId) {
          // URL has a specific section - navigate to it
          for (const lesson of sortedLessons) {
            const sections = lesson?.sections || [];
            const foundSection = sections.find(s => s.sectionId === initialSectionId);
            if (foundSection) {
              targetLesson = lesson;
              break;
            }
          }
        } else {
          // No specific section in URL - find the first uncompleted section to resume
          let foundUncompleted = false;
          for (const lesson of sortedLessons) {
            const sortedSections = (lesson?.sections || []).slice().sort((a, b) => a.order - b.order);
            for (const section of sortedSections) {
              // Check if section is not viewed and not passed (for quizzes)
              const isCompleted = section.viewed === true || section.passed === true;
              if (!isCompleted) {
                targetLesson = lesson;
                targetSectionId = section.sectionId;
                foundUncompleted = true;
                break;
              }
            }
            if (foundUncompleted) break;
          }
          // If all sections are completed, default to first section
          if (!foundUncompleted && firstSec) {
            targetLesson = firstLesson;
            targetSectionId = firstSec.sectionId;
          }
        }

        // If we don't have a lesson loaded yet, or userId changed (ownership change), load the lesson.
        const shouldLoadLesson =
          !lessonView ||
          userIdChanged ||
          (lessonView?.lesson?.lessonId && !outlineRes?.lessons?.some(l => l.lessonId === lessonView.lesson.lessonId));

        if (targetLesson && shouldLoadLesson) {
          const hardLessonLoad = !lessonView || userIdChanged;
          if (hardLessonLoad) {
            setLoadingLesson(true);           // only show "hard" loader if no content to keep painted
          } else {
            setSoftLoadingLesson(true);       // otherwise do a background refresh
          }

          let lv = await safeFetchLesson(userId, targetLesson.lessonId);
          if (!alive) return;

          // Normalize sections so UI can reliably read viewed + contentType
          try {
            if (Array.isArray(lv?.sections)) {
              lv.sections = lv.sections.map((it) => {
                const section = it?.section ? { ...it.section } : { ...it };
                const viewed = (section.viewed !== undefined)
                  ? section.viewed
                  : (it.viewed !== undefined ? it.viewed : false);
                const contentType = section.contentType || it.contentType || it?.content?.type || null;
                return {
                  ...it,
                  section: {
                    ...section,
                    viewed,
                    contentType,
                  }
                };
              });
            }
          } catch { }

          // Add quiz and knowledge check sections from outline if missing from lesson view
          if (outlineRes?.lessons && lv) {
            const lessonFromOutline = outlineRes.lessons.find(l => l.lessonId === targetLesson.lessonId);
            if (lessonFromOutline?.sections) {
              for (const outlineSec of lessonFromOutline.sections) {
                const existsInLv = lv.sections?.some(s =>
                  (s?.section?.sectionId || s?.sectionId) === outlineSec.sectionId
                );
                // Handle QUIZ sections
                if (!existsInLv && outlineSec.contentType === 'QUIZ') {
                  if (!lv.sections) lv.sections = [];
                  lv.sections.push({
                    section: {
                      sectionId: outlineSec.sectionId,
                      title: outlineSec.title,
                      order: outlineSec.order,
                      contentType: 'QUIZ',
                      viewed: outlineSec.viewed || false
                    },
                    content: {
                      type: 'QUIZ'
                    }
                  });
                }
                // Handle KNOWLEDGE_CHECK sections
                if (!existsInLv && outlineSec.contentType === 'KNOWLEDGE_CHECK') {
                  if (!lv.sections) lv.sections = [];
                  lv.sections.push({
                    section: {
                      sectionId: outlineSec.sectionId,
                      title: outlineSec.title,
                      order: outlineSec.order,
                      contentType: 'KNOWLEDGE_CHECK',
                      viewed: outlineSec.viewed || false,
                      passed: outlineSec.passed || false
                    },
                    content: {
                      type: 'KNOWLEDGE_CHECK'
                    }
                  });
                }
              }
              if (lv.sections) {
                lv.sections.sort((a, b) =>
                  (a?.section?.order || 0) - (b?.section?.order || 0)
                );
              }
            }
          }

          setLessonView(lv);

          // Set the selected section (either from URL, or first uncompleted, or first section)
          if (targetSectionId) {
            setSelectedSectionId(targetSectionId);
          }
        }
      } catch (e) {
        if (alive) setStatus(e?.message || 'Failed to load outline');
      } finally {
        if (!alive) return;
        setLoadingOutline(false);
        setSoftLoadingOutline(false);
        setLoadingLesson(false);
        setSoftLoadingLesson(false);
      }
    })();

    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authEnabled, ready, user, courseId, userId, initialSectionId /* outline, lessonView intentionally omitted to avoid refetch churn */]);

  const loadLessonAndSelect = useCallback(
    async (lessonId, sectionIdToSelect, options = {}) => {
      if (authEnabled && (!ready || !user)) return;
      try {
        // If we already have something on screen, prefer a soft load.
        const hard = !lessonView;
        if (hard) setLoadingLesson(true); else setSoftLoadingLesson(true);

        // Fetch the lesson structure
        let lv = await safeFetchLesson(userId, lessonId);

        // --- Normalize sections so UI can reliably read viewed + contentType ---
        try {
          if (Array.isArray(lv?.sections)) {
            lv.sections = lv.sections.map((it) => {
              const section = it?.section ? { ...it.section } : { ...it };
              const viewed = (section.viewed !== undefined)
                ? section.viewed
                : (it.viewed !== undefined ? it.viewed : false);
              const contentType = section.contentType || it.contentType || it?.content?.type || null;
              return {
                ...it,
                section: {
                  ...section,
                  viewed,
                  contentType,
                }
              };
            });
          }
        } catch { }

        // Check if outline has quiz or knowledge check sections that are missing from the lesson view
        // The backend may not return these sections in /lessons/view, but they exist in the outline
        if (outline?.lessons && lv) {
          const lessonFromOutline = outline.lessons.find(l => l.lessonId === lessonId);
          if (lessonFromOutline?.sections) {
            for (const outlineSec of lessonFromOutline.sections) {
              // Check if this section exists in our lesson view
              const existsInLv = lv.sections?.some(s =>
                (s?.section?.sectionId || s?.sectionId) === outlineSec.sectionId
              );

              // If it doesn't exist and it's a quiz, add a placeholder
              if (!existsInLv && outlineSec.contentType === 'QUIZ') {
                if (!lv.sections) lv.sections = [];
                lv.sections.push({
                  section: {
                    sectionId: outlineSec.sectionId,
                    title: outlineSec.title,
                    order: outlineSec.order,
                    contentType: 'QUIZ',
                    viewed: outlineSec.viewed || false
                  },
                  content: {
                    type: 'QUIZ'
                  }
                });
              }

              // If it doesn't exist and it's a knowledge check, add a placeholder
              if (!existsInLv && outlineSec.contentType === 'KNOWLEDGE_CHECK') {
                if (!lv.sections) lv.sections = [];
                lv.sections.push({
                  section: {
                    sectionId: outlineSec.sectionId,
                    title: outlineSec.title,
                    order: outlineSec.order,
                    contentType: 'KNOWLEDGE_CHECK',
                    viewed: outlineSec.viewed || false,
                    passed: outlineSec.passed || false
                  },
                  content: {
                    type: 'KNOWLEDGE_CHECK'
                  }
                });
              }
            }
            // Re-sort sections by order
            if (lv.sections) {
              lv.sections.sort((a, b) =>
                (a?.section?.order || 0) - (b?.section?.order || 0)
              );
            }
          }
        }

        setLessonView(lv);

        if (sectionIdToSelect) {
          setSelectedSectionId(sectionIdToSelect);
        } else {
          const first = lv?.sections?.slice().sort((a, b) => (a?.section?.order || 0) - (b?.section?.order || 0))[0];
          if (first?.section?.sectionId) setSelectedSectionId(first.section.sectionId);
        }
      } catch (e) {
        setStatus(String(e?.message || 'Failed to load lesson'));
      } finally {
        setLoadingLesson(false);
        setSoftLoadingLesson(false);
      }
    },
    [authEnabled, ready, user, lessonView, outline, safeFetchLesson, userId]
  );

  const nav = useMemo(() => lessonView?.navigation || {}, [lessonView]);

  const goPrev = useCallback(() => {
    // "Previous" walks section-by-section, crossing lesson boundaries backwards:
    // the section before a lesson's first section is the LAST section of the
    // previous lesson (5.1 -> 4.N), not its first (4.1). Derived from the ordered
    // outline (see previousSectionOf) so it's correct regardless of how the
    // per-section server nav is populated — that path only stepped to the
    // previous lesson's first section at a boundary.
    const prev = previousSectionOf(outline, selectedSectionId);
    if (prev) {
      if (prev.lessonId === (lessonView?.lesson?.lessonId || null)) {
        setSelectedSectionId(prev.sectionId);
      } else {
        loadLessonAndSelect(prev.lessonId, prev.sectionId);
      }
      return;
    }
    // Fallback when the outline isn't loaded yet or the section isn't found:
    // use the server-provided navigation.
    if (nav.previousSectionId) { setSelectedSectionId(nav.previousSectionId); return; }
    if (nav.previousLessonId) { loadLessonAndSelect(nav.previousLessonId); }
  }, [outline, selectedSectionId, lessonView, nav, loadLessonAndSelect]);

  const goNext = useCallback(() => {
    if (nav.nextSectionId) { setSelectedSectionId(nav.nextSectionId); return; }
    if (nav.nextLessonId) { loadLessonAndSelect(nav.nextLessonId); }
  }, [nav, loadLessonAndSelect]);

  const lessonId = lessonView?.lesson?.lessonId || null;

  // Derive if the entire course is completed (all sections viewed/passed)
  const isCourseCompleted = useMemo(() => {
    if (!outline?.lessons?.length) return false;
    for (const lesson of outline.lessons) {
      const sections = lesson?.sections || [];
      for (const section of sections) {
        const isCompleted = section.viewed === true || section.passed === true;
        if (!isCompleted) return false;
      }
    }
    return true;
  }, [outline]);

  // -------- Polling state/refs (30s) --------
  const outlineRef = useRef(outline);
  useEffect(() => { outlineRef.current = outline; }, [outline]);

  // Store onNewContent in a ref to avoid effect restarts when the callback reference changes
  const onNewContentRef = useRef(onNewContent);
  useEffect(() => { onNewContentRef.current = onNewContent; }, [onNewContent]);

  // ---- POLLING EFFECT (30s with error backoff, only when tab is visible) ----
  useEffect(() => {
    if (!pollOutlineMs) return;
    if (!courseId) return;

    let alive = true;
    let timer = null;
    let delay = pollOutlineMs;            // starts at 30s
    const maxDelay = 60000;               // cap at 60s

    const clearTimer = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const schedule = () => {
      if (!alive) return;
      clearTimer();
      timer = setTimeout(loop, delay);
    };

    const loop = async () => {
      if (!alive) return;

      // Gate on auth readiness when auth is required
      if (authEnabled && (!ready || !user)) { schedule(); return; }

      // Don't poll if tab is hidden (visibility handler will restart when visible)
      if (document.hidden) return;

      try {
        setSoftLoadingOutline(true); // soft indicator only; keep sidebar painted
        const next = await safeFetchOutline(userId);
        if (!alive) return;

        if (outlinesDifferent(outlineRef.current, next)) {
          setOutline(next);

          if (typeof onNewContentRef.current === 'function') {
            try {
              onNewContentRef.current(next);
            } catch (e) {
              console.error('onNewContent callback failed', e);
            }
          }
        }

        delay = pollOutlineMs; // reset on success
      } catch (e) {
        // Log but don't crash - polling failures are expected when offline
        console.warn('[useCourseLearn] Polling failed:', e?.message || e);
        delay = Math.min(delay * 2, maxDelay); // backoff
      } finally {
        if (alive && !document.hidden) {
          setSoftLoadingOutline(false);
          schedule();
        } else {
          setSoftLoadingOutline(false);
        }
      }
    };

    // Handle visibility changes - pause/resume polling
    const onVisibilityChange = () => {
      if (!alive) return;

      if (document.hidden) {
        // Tab became hidden - stop polling
        clearTimer();
      } else {
        // Tab became visible - resume polling immediately
        loop();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    // Start polling only if tab is currently visible
    if (!document.hidden) {
      schedule();
    }

    return () => {
      alive = false;
      clearTimer();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [
    pollOutlineMs,
    courseId,
    authEnabled,
    ready,
    user,
    userId,
    safeFetchOutline,
    // onNewContent intentionally omitted - accessed via onNewContentRef to prevent effect restarts
  ]);

  return {
    // data
    outline,
    loadingOutline,                 // show only for first paint
    softLoadingOutline,             // subtle inline indicator during polling
    lessonView,
    loadingLesson,                  // show only when nothing is painted
    softLoadingLesson,              // subtle inline indicator

    selectedSectionId,
    setSelectedSectionId,
    status,
    ownershipWarning,

    // ids
    lessonId,

    // actions
    loadLessonAndSelect,
    goPrev,
    goNext,

    // completion
    isCourseCompleted,

    // env
    userId,
  };
}